import useQueryParams from "hooks/useQueryParams";
import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
// @material-ui/core components
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { useIntl } from "react-intl";
import { Link, useHistory } from "react-router-dom";
//MaterialUI
import Box from "@material-ui/core/Box";
import IconButton from "@material-ui/core/IconButton";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Tooltip from "@material-ui/core/Tooltip";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowForwardIosIcon from "@material-ui/icons/ArrowForwardIos";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import Brightness1RoundedIcon from "@material-ui/icons/Brightness1Rounded";
import UnfoldMoreIcon from "@material-ui/icons/UnfoldMore";
import Pagination from "@material-ui/lab/Pagination";
import componentStylesGeneric from "assets/theme/views/admin/generic";
import TableBackdrop from "components/Backdrops/TableBackdrop/TableBackdrop";
import GenericButton from "components/Buttons/GenericButton";
import CardTableContainer from "components/Cards/Tables/CardTableContainer";
import TableStateChip from "components/Cards/Tables/TableStateChip";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import { getMyApplicationsAction } from "redux/actions/applicationsAction";
import { titles } from "resources/resources";
import {
    APPLICATION_STATES_APPROVED,
    APPLICATION_STATES_CANCELED,
    APPLICATION_STATES_IN_PROGRESS,
    APPLICATION_STATES_KEY,
    PAGINATION,
    TABLE_STATE_CHIP_COLOR,
} from "utils/const";
import CancelApplicationModal from "views/admin/GovernmentProgram/Application/Modals/CancelApplicationModal";

const useStylesGeneric = makeStyles(componentStylesGeneric);

function MyGovernmentProgramsApplicationsTab(props) {
  const theme = useTheme();
  const classes = useStylesGeneric();
  const history = useHistory();
  const { getQueryParam, setQueryParams, getQueryParams } = useQueryParams();
  const qpPrefix = useMemo(() => "myGovApps_", []);
  const intl = useIntl();
  const [cancelRequest, setCancelRequest] = useState(false);
  const [applicationNumber, setApplicationNumber] = useState("");
  const [pageIndex, setPageIndex] = useState(() => {
    const fromUrl = getQueryParam(`${qpPrefix}pageIndex`);
    return fromUrl ? Number(fromUrl) : PAGINATION.FIRST_PAGE;
  });
  const [sortColumn, setSortColumn] = useState(() => getQueryParam(`${qpPrefix}sortColumn`) || null);
  const [sortDirection, setSortDirection] = useState(() => getQueryParam(`${qpPrefix}sortDirection`) || "asc");

  const handleChangePage = (event, value) => {
    setPageIndex(value);
  };

  const tableHead = [
    { label: "Programa", sortKey: "governmentProgram" },
    { label: "Subprograma", sortKey: "program" },
    { label: "Iniciativa", sortKey: "subprogram" },
    { label: "Nº Candidatura", sortKey: "applicationCode" },
    { label: "Estado", sortKey: "stateDescription" },
    { label: ""},
    { label: "" },
  ];

  const handleSort = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    
    if (sortColumn === column) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setPageIndex(PAGINATION.FIRST_PAGE);
  };

  // Sync state -> URL
  useEffect(() => {
    const current = getQueryParams();

    if (pageIndex && pageIndex > 1) current[`${qpPrefix}pageIndex`] = pageIndex;
    else delete current[`${qpPrefix}pageIndex`];

    if (sortColumn) current[`${qpPrefix}sortColumn`] = sortColumn;
    else delete current[`${qpPrefix}sortColumn`];

    if (sortDirection && sortDirection !== "asc") current[`${qpPrefix}sortDirection`] = sortDirection;
    else delete current[`${qpPrefix}sortDirection`];

    setQueryParams(current, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, sortColumn, sortDirection, qpPrefix]);

  // URL (back/forward) -> state
  useEffect(() => {
    const handlePopState = () => {
      const nextPageIndex = getQueryParam(`${qpPrefix}pageIndex`);
      const nextSortColumn = getQueryParam(`${qpPrefix}sortColumn`);
      const nextSortDirection = getQueryParam(`${qpPrefix}sortDirection`);

      setPageIndex(nextPageIndex ? Number(nextPageIndex) : PAGINATION.FIRST_PAGE);
      setSortColumn(nextSortColumn || null);
      setSortDirection(nextSortDirection || "asc");
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qpPrefix]);

  const renderSortIcon = (columnIndex) => {
    const column = tableHead[columnIndex].sortKey;
    if (sortColumn === column) {
      return sortDirection === "desc" ? (
        <ArrowUpwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      ) : (
        <ArrowDownwardIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle" }} />
      );
    }
    return <UnfoldMoreIcon style={{ fontSize: 16, marginLeft: 4, verticalAlign: "middle", opacity: 0.3 }} />;
  };

  const mapRecentApplicationsDashboardChipColors = (stateKey) => {
    if (APPLICATION_STATES_APPROVED.includes(stateKey)) {
      return TABLE_STATE_CHIP_COLOR.GREEN;
    }
    if (APPLICATION_STATES_CANCELED.includes(stateKey)) {
      return TABLE_STATE_CHIP_COLOR.RED;
    }
    if (APPLICATION_STATES_IN_PROGRESS.includes(stateKey)) {
      return TABLE_STATE_CHIP_COLOR.LIGHT_ORANGE;
    }
  };

  useEffect(() => {
    const filter = {
      pageIndex: pageIndex,
      pageSize: PAGINATION.PAGE_SIZE,
      sortColumn: sortColumn,
      sortDirection: sortDirection,
    };

    props.getMyApplications(filter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageIndex, sortColumn, sortDirection]);

  function handleOpenCancelApplicationModalEvent(applicationNumber) {
    setApplicationNumber(applicationNumber);
    setCancelRequest(true);
  }

  function handleCloseCancelApplicationModalEvent() {
    setCancelRequest(false);
  }

  return (
    <>
      <CardTableContainer
        title={intl.formatMessage(titles.Title_Table_UserSingular_ProgramApplications)}
        count={props?.applicationsState?.result?.total_records}
      >

        <TableContainer>
          <Box component={Table} alignItems="center" marginBottom="0!important">
            <TableHead>
              <TableRow>
                {tableHead.map((prop, key) => (
                  <TableCell
                    key={key}
                    classes={{
                      root: classes.tableCellRoot + " " + classes.tableCellRootHead,
                    }}
                    onClick={() => handleSort(key)}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    <Box display="flex" alignItems="center">
                      {prop.label}
                      {renderSortIcon(key)}
                    </Box>
                  </TableCell>
                ))}
                <TableCell
                  classes={{
                    root: classes.tableCellRoot + " " + classes.tableCellRootHead,
                  }}
                >
                </TableCell>
                <TableCell
                  classes={{
                    root: classes.tableCellRoot + " " + classes.tableCellRootHead,
                  }}
                >
                </TableCell>
                <TableCell
                  key="detailsCol"
                  classes={{
                    root:
                      classes.tableCellRoot + " " + classes.tableCellRootHead + " " + classes.tableCellHeadStickyRight,
                  }}
                >
                  Detalhes
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {props?.applicationsState?.result?.data?.map((prop, key) => (
                <TableRow key={key}>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box fontWeight={"bold"}>{prop?.governmentProgram}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.program}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.subprogram}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <Box>{prop?.id}</Box>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <TableStateChip
                      state={mapRecentApplicationsDashboardChipColors(prop?.state?.key)}
                      message={prop?.state?.description}
                    />
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    {prop.state.key === APPLICATION_STATES_KEY.AGUARDA_INFORMACAO && (
                      <GenericButton
                        typeSubmit={false}
                        color="tertiary"
                        size="small"
                        onClick={() => {
                          history.push(`/admin/edit-application/${prop.id}`);
                        }}
                      >
                        <Brightness1RoundedIcon classes={{ root: classes.pendingIcon }} />
                        Adicionar Dados Em Falta
                      </GenericButton>
                    )}
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot }}>
                    <GenericButton
                      typeSubmit={false}
                      color="tertiary"
                      size="small"
                      disabled={
                        prop.state.key === APPLICATION_STATES_KEY.CANCELADA ||
                          prop.state.key === APPLICATION_STATES_KEY.REJEITADA
                          ? true
                          : false
                      }
                      onClick={() => {
                        handleOpenCancelApplicationModalEvent(prop.id);
                      }}
                    >
                      Cancelar
                    </GenericButton>
                  </TableCell>
                  <TableCell classes={{ root: classes.tableCellRoot + " " + classes.tableCellBodyStickyRight }}>
                    <Tooltip title="Detalhes">
                      <IconButton aria-label="details" component={Link} to={`/admin/application-details/${prop.id}`}>
                        <Box
                          component={ArrowForwardIosIcon}
                          width="12px!important"
                          height="12px!important"
                          color={theme.palette.primary.main}
                        />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Box>
          <TableBackdrop open={props?.applicationsState?.loading} />
        </TableContainer>

        <Pagination
          className={classes.alignPagination}
          count={props?.applicationsState?.result?.total_pages}
          page={pageIndex}
          onChange={handleChangePage}
          color="primary"
        ></Pagination>
      </CardTableContainer>

      <GenericDialog maxWidth={"sm"} open={cancelRequest} onClose={handleCloseCancelApplicationModalEvent}>
        <CancelApplicationModal
          applicationNumber={applicationNumber}
          inProgress={false}
          onClose={handleCloseCancelApplicationModalEvent}
        />
      </GenericDialog>
    </>
  );
}
const mapStateToProps = (state) => ({ ...state });

const mapDispatchToProps = (dispatch) => ({
  getMyApplications: (data) => dispatch(getMyApplicationsAction(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(MyGovernmentProgramsApplicationsTab);
